import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Alert, Paper, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowParams, GridToolbar } from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../services/api';

interface UserRow {
  _id: string;
  email: string;
  createdAt?: string;
  updatedAt?: string;
}

const UserManagement: React.FC = () => {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [toDelete, setToDelete] = useState<UserRow | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUsers();
      setRows((data || []).map((u) => ({
        _id: u._id,
        email: u.email,
        createdAt: u.createdAt,
        updatedAt: u.updatedAt
      })));
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setEmail('');
    setPassword('');
    setDialogOpen(true);
  };

  const openEdit = (row: UserRow) => {
    setEditing(row);
    setEmail(row.email);
    setPassword('');
    setDialogOpen(true);
  };

  const submit = async () => {
    try {
      setError(null);
      if (editing) {
        await api.updateUserEmail(editing._id, email);
        setSuccess('User email updated');
      } else {
        if (!password) {
          setError('Password is required');
          return;
        }
        await api.createUser(email, password);
        setSuccess('User created');
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Operation failed');
    }
  };

  const confirmDelete = (row: UserRow) => {
    setToDelete(row);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.deleteUser(toDelete._id);
      setSuccess('User deleted');
      setDeleteOpen(false);
      setToDelete(null);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Delete failed');
    }
  };

  const columns: GridColDef[] = [
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'createdAt', headerName: 'Created', width: 180,
      renderCell: (p) => (p.row.createdAt ? new Date(p.row.createdAt).toLocaleString() : '')
    },
    {
      field: 'updatedAt', headerName: 'Updated', width: 180,
      renderCell: (p) => (p.row.updatedAt ? new Date(p.row.updatedAt).toLocaleString() : '')
    },
    {
      field: 'actions', type: 'actions', headerName: 'Actions', width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => openEdit(params.row as UserRow)} />,
        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => confirmDelete(params.row as UserRow)} />,
      ]
    }
  ];

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h4">User Management</Typography>
          <Box>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ ml: 1 }}>Add User</Button>
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
        <Box sx={{ height: 520, mt: 2 }}>
          <DataGrid
            rows={rows}
            getRowId={(r) => r._id}
            columns={columns}
            loading={loading}
            slots={{ toolbar: GridToolbar }}
            slotProps={{ toolbar: { showQuickFilter: true } }}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          />
        </Box>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit User Email' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {!editing && (
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Delete user "{toDelete?.email}"? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} startIcon={<DeleteIcon />}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;


